import { calendarDateTime, type StayBanner } from "@calendar/calendarLayout";
import { summarizeCalendarGroup } from "@calendar/groupCalendarVisits";
import { cn } from "@scribenote/brian-design-system";
import type { TodayVisit } from "@visits/visitsClient";
import { useLayoutEffect, useRef, useState } from "react";

export const AllDayTray = ({
  stays,
  dayCount,
  timeZone,
  scrollbarWidth,
  onSelect,
}: {
  stays: StayBanner[];
  dayCount: number;
  timeZone: string;
  scrollbarWidth: number;
  onSelect: (visit: TodayVisit) => void;
}) => {
  const trayRef = useRef<HTMLElement>(null);
  const [trayScrollbarWidth, setTrayScrollbarWidth] = useState(0);
  useLayoutEffect(() => {
    const element = trayRef.current;
    if (!element) return;
    const measure = () =>
      setTrayScrollbarWidth(element.offsetWidth - element.clientWidth);
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    measure();
    return () => observer.disconnect();
  }, [stays.length]);
  const endPadding = Math.max(0, scrollbarWidth - trayScrollbarWidth);
  if (!stays.length) return null;
  return (
    <section
      ref={trayRef}
      aria-label="All-day and multi-day appointments"
      className="flex max-h-32 shrink-0 overflow-y-auto border-b border-rule bg-paper"
    >
      <div aria-hidden="true" className="w-14 shrink-0" />
      <div
        className="grid min-w-0 flex-1 auto-rows-[32px] gap-y-1 py-1.5"
        style={{
          gridTemplateColumns: `repeat(${dayCount}, minmax(0, 1fr))`,
          paddingRight: endPadding,
        }}
      >
        {stays.map((stay) => {
          const visit = stay.group.visits[0];
          const { title, cancelled, accentStatus } = summarizeCalendarGroup(
            stay.group,
          );
          const name =
            title ??
            [visit.patientName, visit.clientName].filter(Boolean).join(" · ");
          const range = `${calendarDateTime(visit.scheduledStart!, timeZone)} - ${calendarDateTime(visit.scheduledEnd!, timeZone)}`;
          return (
            <button
              key={stay.group.visits.map((v) => v.id).join("-")}
              type="button"
              onClick={() => onSelect(visit)}
              aria-label={`${name}, ${visit.visitType}, ${range}${cancelled ? ", cancelled" : ""}`}
              title={`${name}\n${visit.reason ?? visit.visitType}\n${range}`}
              data-dd-privacy="hidden"
              style={{
                gridColumn: `${stay.firstDay + 1} / ${stay.lastDay + 2}`,
                gridRow: stay.row + 1,
                marginRight: stay.continuesAfter ? -endPadding : undefined,
              }}
              className={cn(
                "@container mx-[3px] flex min-w-0 cursor-pointer items-center gap-2 overflow-hidden rounded-[4px] border border-l-[3px] border-rule bg-card px-[7px] text-left text-ink shadow-[0_1px_0_var(--color-rule)]",
                accentStatus === "active"
                  ? "border-l-ink-working"
                  : accentStatus === "done"
                    ? "border-l-annotation"
                    : "border-l-hollow",
                "transition hover:-translate-y-px hover:shadow-lift-lg motion-reduce:transition-none",
                "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent",
                stay.continuesBefore &&
                  "-ml-14 rounded-l-none border-l-0 pl-[63px]",
                stay.continuesAfter && "rounded-r-none border-r-0",
                cancelled && "opacity-60",
              )}
            >
              <span
                className={cn(
                  "min-w-0 truncate text-[13px] font-semibold",
                  cancelled && "line-through",
                )}
              >
                {name}
              </span>
              <span className="hidden shrink-0 font-mono text-[10px] text-annotation @[240px]:inline">
                {visit.visitType}
              </span>
              <span className="ml-auto hidden min-w-0 truncate font-mono text-[10px] text-annotation @[560px]:inline">
                {range}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
};
